import classNames from "classnames";
import { Icon, IconsType } from "./Icon";

/*
 * "Milestone path" redesign — circular milestone nodes on a connecting
 * path, replacing the flat bordered-card design. Full visual rework
 * (shadows, rings, motion), same external API as before:
 * TProcessItem/ProcessProps unchanged — title, description, icon,
 * current, color, line, lineColor, onClick, disabled, statusLabel all
 * still do what they did before. Nothing calling <Process data={...}/>
 * needs to change.
 *
 * Notable behavior changes from the previous version:
 * - `line` now genuinely supports "dashed"/"dotted"/"double" (previously
 *   only truthy/falsy was read).
 * - `lineColor` is now actually used for the connector's completed color,
 *   falling back to the step's own `color` when omitted (previously
 *   accepted but ignored).
 * - Steps with no `icon` show their step number as a fallback instead of
 *   rendering an empty circle.
 * - Respects prefers-reduced-motion (Tailwind's `motion-reduce:` variant)
 *   by disabling the current-step pulse ring.
 *
 * The connector is anchored to each node's own fixed-size wrapper
 * (absolute position, not a separate flex sibling) — a plain sibling
 * connector between flex items was confirmed unreliable in production
 * earlier; anchoring to a fixed box on the node itself doesn't depend on
 * how flex resolved anyone else's size.
 *
 * Icon size bumped from "sm" to "md" to match the larger circles — "sm"
 * was already confirmed to work elsewhere, "md" is assumed to exist on
 * the same scale (xs/sm/...); check it renders correctly.
 */

const CIRCLE_BOX = 64; // px — fixed regardless of visual state, so the connector's anchor point never shifts
const CONNECTOR_WIDTH = 96; // px

const PROCESS_COLORS = {
    blue: {
        bg: "bg-blue",
        border: "border-blue",
        tint: "bg-blue/10",
        text: "text-blue",
        ring: "ring-blue/25",
        ringSoft: "ring-blue/15",
        pulseBg: "bg-blue/30",
    },
    red: {
        bg: "bg-red",
        border: "border-red",
        tint: "bg-red/10",
        text: "text-red",
        ring: "ring-red/25",
        ringSoft: "ring-red/15",
        pulseBg: "bg-red/30",
    },
    yellow: {
        bg: "bg-yellow",
        border: "border-yellow",
        tint: "bg-yellow/10",
        text: "text-yellow",
        ring: "ring-yellow/25",
        ringSoft: "ring-yellow/15",
        pulseBg: "bg-yellow/30",
    },
    green: {
        bg: "bg-green",
        border: "border-green",
        tint: "bg-green/10",
        text: "text-green",
        ring: "ring-green/25",
        ringSoft: "ring-green/15",
        pulseBg: "bg-green/30",
    },
};

export type TProcessItem = {
    title?: string;
    description?: string;
    icon?: IconsType;
    current?: boolean;
    line?: boolean | "dashed" | "dotted" | "double";
    color?: keyof typeof PROCESS_COLORS;
    lineColor?: keyof typeof PROCESS_COLORS;
    onClick?: () => void;
    disabled?: boolean;
    statusLabel?: string;
};

type ProcessProps = {
    data?: TProcessItem[];
};

const lineStyleClass = (variant: TProcessItem["line"]) =>
    variant === "dashed" ? "border-dashed" : variant === "dotted" ? "border-dotted" : variant === "double" ? "border-double border-t-[3px]" : "border-solid";

export const Process = ({ data }: ProcessProps) => {
    if (!data || data.length === 0) return null;

    return (
        <div className="w-full overflow-x-auto">
            <div className="flex justify-center items-start gap-8 sm:gap-10 px-4 py-8 min-w-max mx-auto">
                {data.map((item, index) => {
                    const {
                        title,
                        description,
                        icon,
                        current,
                        color = "blue",
                        line = true,
                        lineColor,
                        onClick,
                        disabled,
                        statusLabel,
                    } = item;

                    const isError = !current && color === "red";
                    const isDone = !current && !isError && color === "green";
                    const isLast = index === data.length - 1;
                    const showConnector = Boolean(line) && !isLast;
                    const clickable = Boolean(onClick) && !disabled;
                    const palette = PROCESS_COLORS[color];
                    const connectorPalette = PROCESS_COLORS[lineColor ?? color];

                    const mainLabel = description || title;
                    const showBadge = Boolean(statusLabel) || current || isDone || isError;
                    const badgeText = statusLabel ?? (current ? "In progress" : isDone ? "Done" : "Needs attention");

                    return (
                        <div key={index} className="relative flex flex-col items-center shrink-0 w-32 sm:w-36">
                            {showConnector && (
                                <div
                                    aria-hidden="true"
                                    style={{
                                        position: "absolute",
                                        top: CIRCLE_BOX / 2,
                                        left: "100%",
                                        width: CONNECTOR_WIDTH,
                                    }}
                                    className={classNames(
                                        "border-t-[3px] transition-colors duration-500",
                                        lineStyleClass(line),
                                        isDone ? connectorPalette.border : "border-gray-200",
                                    )}
                                />
                            )}

                            <div
                                className="relative flex items-center justify-center shrink-0"
                                style={{ width: CIRCLE_BOX, height: CIRCLE_BOX }}
                            >
                                {current && (
                                    <span
                                        aria-hidden="true"
                                        className={classNames(
                                            "absolute inset-1 rounded-full animate-ping motion-reduce:hidden",
                                            palette.pulseBg,
                                        )}
                                    />
                                )}

                                <button
                                    type="button"
                                    disabled={!clickable}
                                    onClick={clickable ? onClick : undefined}
                                    onKeyDown={
                                        clickable
                                            ? (e) => {
                                                if (e.key === "Enter" || e.key === " ") {
                                                    e.preventDefault();
                                                    onClick?.();
                                                }
                                            }
                                            : undefined
                                    }
                                    aria-current={current ? "step" : undefined}
                                    tabIndex={clickable ? 0 : -1}
                                    className={classNames(
                                        "relative rounded-full flex items-center justify-center transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue",
                                        current
                                            ? classNames("w-16 h-16 shadow-lg ring-4", palette.bg, palette.ring)
                                            : isDone
                                                ? classNames("w-[60px] h-[60px] shadow-md ring-4", palette.bg, palette.ringSoft)
                                                : isError
                                                    ? classNames("w-14 h-14 bg-white border-2 shadow-sm ring-4", palette.border, palette.ringSoft)
                                                    : "w-14 h-14 bg-white border-2 border-gray-300 opacity-95",
                                        clickable && "cursor-pointer hover:-translate-y-0.5 hover:shadow-lg",
                                        disabled && "cursor-not-allowed opacity-50",
                                    )}
                                >
                                    {isDone ? (
                                        <Icon icon="completed" size="md" className="text-white" />
                                    ) : icon ? (
                                        <Icon
                                            icon={icon}
                                            size="md"
                                            className={current ? "text-white" : isError ? palette.text : "text-gray-400"}
                                        />
                                    ) : (
                                        <span
                                            className={classNames(
                                                "text-base font-semibold",
                                                current ? "text-white" : isError ? palette.text : "text-gray-400",
                                            )}
                                        >
                                            {index + 1}
                                        </span>
                                    )}
                                </button>
                            </div>

                            <div className="mt-3 text-center px-0.5">
                                {mainLabel && (
                                    <div
                                        className={classNames(
                                            "text-[13px] leading-snug whitespace-nowrap",
                                            current
                                                ? "font-semibold text-gray-900"
                                                : isDone
                                                    ? "font-medium text-gray-700"
                                                    : isError
                                                        ? classNames("font-medium", palette.text)
                                                        : "text-gray-500",
                                        )}
                                    >
                                        {mainLabel}
                                    </div>
                                )}
                                {showBadge && (
                                    <span
                                        className={classNames(
                                            "inline-block mt-1.5 text-[10px] font-medium transition-colors duration-300",
                                            palette.text,
                                        )}
                                    >
                                        {badgeText}
                                    </span>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};